import { BESTIARY_TIERS, type BestiaryPageState, MOBS } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import type { AtlasFrame } from './doll-preview.js';
import { element } from './kit.js';
import { itemSlot } from './quest-rewards.js';
import { uiFormat, uiNumber, uiPercent, uiText } from './text.js';

/** The portrait's box: a 32 px creature shows doubled, a 48 or 72 px one as it is. */
const PORTRAIT_BOX = 72;

/** The idle frame, facing south, of a body: the scene's own textures, or null before they load. */
export type CreatureFrame = (bodyKey: string) => AtlasFrame | null;

const KIND_LABELS = { creature: '', elite: 'Élite', boss: 'Boss' } as const;

/**
 * The creature as it stands in the world, on whole pixels; a creature never
 * slain is its shape alone, filled black.
 */
function portrait(page: BestiaryPageState, frameOf: CreatureFrame): HTMLElement {
  const box = element('span', 'bestiary__portrait');
  const frame = frameOf(MOBS[page.mob].bodyKey);
  if (frame === null) return box;
  const canvas = document.createElement('canvas');
  canvas.width = frame.width;
  canvas.height = frame.height;
  const context = canvas.getContext('2d');
  if (context === null) return box;
  context.imageSmoothingEnabled = false;
  context.drawImage(
    frame.image,
    frame.x,
    frame.y,
    frame.width,
    frame.height,
    0,
    0,
    frame.width,
    frame.height,
  );
  if (page.hidden) {
    context.globalCompositeOperation = 'source-in';
    context.fillStyle = '#000000';
    context.fillRect(0, 0, frame.width, frame.height);
  }
  const scale = Math.max(1, Math.floor(PORTRAIT_BOX / Math.max(frame.width, frame.height)));
  canvas.style.width = `${String(frame.width * scale)}px`;
  canvas.style.height = `${String(frame.height * scale)}px`;
  canvas.setAttribute('aria-hidden', 'true');
  box.append(canvas);
  return box;
}

/** The tier as three marks, the reached ones lit, and what the best one adds to the blows. */
function tierLine(page: BestiaryPageState): HTMLElement {
  const line = element('div', 'bestiary__tier');
  const marks = element('span', 'bestiary__marks');
  marks.setAttribute('aria-hidden', 'true');
  BESTIARY_TIERS.forEach((_, index) => {
    const mark = element('span', 'bestiary__mark');
    mark.classList.toggle('bestiary__mark--lit', index < page.tier);
    marks.append(mark);
  });
  const bonus = BESTIARY_TIERS[page.tier - 1]?.damageBonus;
  const text = element(
    'span',
    'bestiary__tier-text',
    bonus === undefined
      ? uiText('Aucun palier')
      : uiFormat('Palier {tier} · +{bonus} de dégâts', {
          tier: page.tier,
          bonus: uiPercent(Math.round(bonus * 100)),
        }),
  );
  line.append(marks, text);
  return line;
}

/** One page of the log, as the server's career fills it (`bestiaryOf`). */
export function bestiaryPage(
  page: BestiaryPageState,
  frameOf: CreatureFrame,
  icons: CssAtlas | null,
): HTMLElement {
  const mob = MOBS[page.mob];
  const card = element('article', 'bestiary__page');
  card.classList.toggle('bestiary__page--hidden', page.hidden);
  // The last tier reached: a gold frame, drawn here, ruled by nothing.
  card.classList.toggle('bestiary__page--gold', page.next === null);
  card.dataset.kind = page.kind;
  if (!page.hidden) card.dataset.rarity = mob.rarity;

  const body = element('div', 'bestiary__body');
  const head = element('div', 'bestiary__head');
  const name = element('span', 'bestiary__name', page.hidden ? '???' : mob.name);
  head.append(name);
  const kind = KIND_LABELS[page.kind];
  if (kind !== '') head.append(element('span', 'bestiary__kind', kind));
  if (mob.level !== undefined) {
    head.append(
      element('span', 'bestiary__level', uiFormat('Niv. {level}', { level: mob.level })),
    );
  }

  const kills = element(
    'div',
    'bestiary__kills',
    page.next === null
      ? uiFormat('{kills} vaincus · palier maximal', { kills: uiNumber(page.kills) })
      : uiFormat('{kills} / {next} vaincus', {
          kills: uiNumber(page.kills),
          next: uiNumber(page.next),
        }),
  );
  body.append(head, kills, tierLine(page));

  if (!page.hidden) {
    const loot = element('div', 'bestiary__loot');
    if (page.loot.length === 0) {
      loot.append(element('span', 'bestiary__none', uiText('Aucun butin vu')));
    } else {
      for (const templateId of page.loot) loot.append(itemSlot({ templateId, quantity: 1 }, icons));
    }
    body.append(loot);
  }

  card.append(portrait(page, frameOf), body);
  return card;
}
